"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { api } from "@/lib/api";
import { invalidate } from "@/lib/use-fetch";
import { parseCsv } from "@/lib/template";
import { downloadCsv, dateStamp, fetchAllPages } from "@/lib/export";
import { useVirtualRows } from "@/lib/use-virtual-rows";
import { Segments } from "@/components/segments";
import { Badge, Button, Card, EmptyState, Input, Modal, PageHeader, Select, Skeleton, Textarea } from "@/components/ui";
import { useToast } from "@/components/toast";
import { useConfirm } from "@/components/confirm";
import type { Contact, ContactGroup } from "@/lib/types";

// Import is sent in chunks (the gateway caps a single request at 1000 and the HTTP body at 1MB).
const IMPORT_CHUNK = 500;
// Contacts are paged so an account with thousands of them isn't silently truncated.
const PAGE_SIZE = 100;
// "Load more" appends, so the list grows without bound — window the rows once it gets big.
// Below this the table renders normally (no scroll box, no fixed row heights).
const VIRTUALIZE_FROM = 300;
const ROW_HEIGHT = 44; // px — must match the rendered row height exactly

export default function ContactsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");   // "" = all contacts
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Add-contact form
  const [msisdn, setMsisdn] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  // Group modal
  const [groupModal, setGroupModal] = useState<{ groupId?: number; name: string; description: string } | null>(null);
  // Import modal
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<string[][]>([]);
  const [phoneCol, setPhoneCol] = useState(0);
  const [nameCol, setNameCol] = useState(-1);
  const [hasHeader, setHasHeader] = useState(true);
  const [importGroup, setImportGroup] = useState("");
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadGroups = useCallback(() => {
    api<{ groups: ContactGroup[] }>("account/contact-groups")
      .then((r) => setGroups(r.groups || []))
      .catch(() => setGroups([]));
  }, []);

  // Fetch one page. offset 0 replaces the list; a later offset appends ("Load more").
  const fetchPage = useCallback(
    async (offset: number) => {
      const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (search.trim()) p.set("search", search.trim());
      if (groupFilter) p.set("groupId", groupFilter);
      const r = await api<{ contacts: Contact[] }>(`account/contacts?${p.toString()}`);
      const page = r.contacts || [];
      setContacts((prev) => (offset === 0 || prev === null ? page : [...prev, ...page]));
      setHasMore(page.length === PAGE_SIZE);
    },
    [search, groupFilter]
  );

  const loadContacts = useCallback(() => {
    fetchPage(0).catch(() => { setContacts([]); setHasMore(false); });
  }, [fetchPage]);

  // Export exactly what the current filters show (search + group), not just the loaded page.
  // The columns are deliberately the ones the importer understands — msisdn, name, then one column
  // per merge variable — so an export can be edited in Excel and imported straight back.
  async function exportCsv() {
    setExporting(true);
    try {
      const all = await fetchAllPages<Contact>(async (offset, limit) => {
        const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        if (search.trim()) p.set("search", search.trim());
        if (groupFilter) p.set("groupId", groupFilter);
        const r = await api<{ contacts: Contact[] }>(`account/contacts?${p.toString()}`);
        const items = r.contacts || [];
        // The endpoint returns no hasMore flag — a short page means we've reached the end.
        return { items, hasMore: items.length === limit };
      });

      if (all.length === 0) {
        toast.error("Nothing to export.");
        return;
      }

      // Union of every attribute key, so a contact missing one still lines up under the right column.
      const attrKeys: string[] = [];
      for (const c of all) {
        for (const k of Object.keys(c.attributes ?? {})) {
          if (!attrKeys.includes(k)) attrKeys.push(k);
        }
      }

      const groupName = groups.find((g) => String(g.groupId) === groupFilter)?.name;
      const slug = groupName ? groupName.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all";

      downloadCsv(
        `contacts-${slug}-${dateStamp()}`,
        ["msisdn", "name", ...attrKeys],
        all.map((c) => [c.msisdn, c.name ?? "", ...attrKeys.map((k) => c.attributes?.[k] ?? "")])
      );
      toast.success(`Exported ${all.length.toLocaleString()} contact(s).`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  async function loadMore() {
    setLoadingMore(true);
    try {
      await fetchPage(contacts?.length ?? 0);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadContacts(); setSelected(new Set()); }, [loadContacts]);

  async function addContact() {
    const m = msisdn.replace(/\D/g, "");
    if (m.length < 6) { toast.error("Enter a valid phone number."); return; }
    setSaving(true);
    try {
      await api("account/contacts", {
        method: "POST",
        body: { msisdn: m, name: name.trim() || null, groupId: groupFilter ? Number(groupFilter) : null }
      });
      toast.success("Contact saved.");
      setMsisdn(""); setName("");
      loadContacts(); loadGroups();
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  }

  async function removeContact(c: Contact) {
    const ok = await confirm({
      title: "Delete contact",
      message: <>Delete <strong>{c.name || c.msisdn}</strong>? They&apos;ll be removed from every group. This can&apos;t be undone.</>,
      confirmLabel: "Delete",
      danger: true
    });
    if (!ok) return;
    try {
      await api("account/contacts", { method: "DELETE", body: { contactId: c.contactId } });
      toast.success("Contact deleted.");
      loadContacts(); loadGroups();
    } catch (e) { toast.error((e as Error).message); }
  }

  async function saveGroup() {
    if (!groupModal?.name.trim()) { toast.error("Enter a group name."); return; }
    try {
      await api("account/contact-groups", {
        method: "POST",
        body: { groupId: groupModal.groupId ?? null, name: groupModal.name.trim(), description: groupModal.description.trim() || null }
      });
      toast.success(groupModal.groupId ? "Group updated." : "Group created.");
      setGroupModal(null);
      loadGroups();
      invalidate("account/contact-groups"); // keep the shared cache (bulk/personalized group picker) fresh
    } catch (e) { toast.error((e as Error).message); }
  }

  async function deleteGroup(g: ContactGroup) {
    const ok = await confirm({
      title: "Delete group",
      message: (
        <>
          Delete the group <strong>{g.name}</strong> ({g.memberCount.toLocaleString()} contact
          {g.memberCount === 1 ? "" : "s"})? The contacts themselves are <strong>kept</strong> — only the
          grouping is removed.
        </>
      ),
      confirmLabel: "Delete group",
      danger: true
    });
    if (!ok) return;
    try {
      await api("account/contact-groups", { method: "DELETE", body: { groupId: g.groupId } });
      toast.success(`Deleted "${g.name}". The contacts themselves were kept.`);
      if (groupFilter === String(g.groupId)) setGroupFilter("");
      loadGroups(); loadContacts();
      invalidate("account/contact-groups");
    } catch (e) { toast.error((e as Error).message); }
  }

  // Add every selected contact to a group.
  async function addSelectedToGroup(groupId: number) {
    if (selected.size === 0) return;
    try {
      const res = await api<{ affected: number }>("account/contact-groups/members", {
        method: "POST",
        body: { groupId, contactIds: [...selected] }
      });
      toast.success(`Added ${res.affected} contact(s) to the group.`);
      setSelected(new Set());
      loadGroups(); loadContacts();
    } catch (e) { toast.error((e as Error).message); }
  }

  async function removeSelectedFromGroup() {
    if (!groupFilter || selected.size === 0) return;
    try {
      const res = await api<{ affected: number }>("account/contact-groups/members", {
        method: "POST",
        body: { groupId: Number(groupFilter), contactIds: [...selected], action: "remove" }
      });
      toast.success(`Removed ${res.affected} contact(s) from the group.`);
      setSelected(new Set());
      loadGroups(); loadContacts();
    } catch (e) { toast.error((e as Error).message); }
  }

  // ---- CSV import ----
  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result || ""));
      if (rows.length === 0) { toast.error("That file has no rows."); return; }
      setImportRows(rows);
      setPhoneCol(0);
      setNameCol(rows[0].length > 1 ? 1 : -1);
      setHasHeader(true);
      setImportOpen(true);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  const importHeaders = importRows[0] ?? [];
  const importBody = useMemo(() => (hasHeader ? importRows.slice(1) : importRows), [importRows, hasHeader]);

  // Every column becomes a merge variable, so {{city}} etc. work on personalized sends.
  const importPreview = useMemo(() => {
    const out: { msisdn: string; name: string | null; attributes: Record<string, string> }[] = [];
    const seen = new Set<string>();
    for (const row of importBody) {
      const m = (row[phoneCol] ?? "").replace(/\D/g, "");
      if (m.length < 6 || seen.has(m)) continue;
      seen.add(m);
      const attributes: Record<string, string> = {};
      importHeaders.forEach((h, i) => {
        const key = (hasHeader ? h : `col${i + 1}`).trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
        if (key && i !== phoneCol) attributes[key] = (row[i] ?? "").trim();
      });
      out.push({ msisdn: m, name: nameCol >= 0 ? (row[nameCol] ?? "").trim() || null : null, attributes });
    }
    return out;
  }, [importBody, importHeaders, phoneCol, nameCol, hasHeader]);

  async function runImport() {
    if (importPreview.length === 0) { toast.error("No valid numbers found in that file."); return; }
    setImporting(true);
    setImportProgress(0);
    try {
      // Send in chunks: a single request with a large CSV would blow past the gateway's 1MB body
      // limit and fail outright. (Bulk SMS batches its recipients the same way.)
      let imported = 0;
      for (let i = 0; i < importPreview.length; i += IMPORT_CHUNK) {
        const batch = importPreview.slice(i, i + IMPORT_CHUNK);
        const res = await api<{ imported: number }>("account/contacts", {
          method: "POST",
          body: { contacts: batch, groupId: importGroup ? Number(importGroup) : null }
        });
        imported += res.imported ?? batch.length;
        setImportProgress(Math.min(i + batch.length, importPreview.length));
      }
      toast.success(`Imported ${imported.toLocaleString()} contact(s).`);
      setImportOpen(false);
      setImportRows([]);
      setImportGroup("");
      loadContacts(); loadGroups();
    } catch (e) { toast.error((e as Error).message); }
    finally { setImporting(false); setImportProgress(0); }
  }

  const allShown = contacts ?? [];
  const allSelected = allShown.length > 0 && selected.size === allShown.length;

  // Only window once the list is actually long enough to matter.
  const virtual = useVirtualRows(scrollRef, {
    count: allShown.length,
    rowHeight: ROW_HEIGHT,
    enabled: allShown.length > VIRTUALIZE_FROM
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Contacts"
        subtitle="Save recipients once and send to a whole group — no more pasting numbers."
        actions={
          <>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
            <Button variant="secondary" onClick={exportCsv} disabled={exporting || (contacts?.length ?? 0) === 0}>
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            <Button variant="secondary" onClick={() => fileRef.current?.click()}>Import CSV</Button>
            <Button onClick={() => setGroupModal({ name: "", description: "" })}>New group</Button>
          </>
        }
      />

      {/* Groups */}
      <Card title="Groups">
        {groups.length === 0 ? (
          <EmptyState
            title="No groups yet"
            description="Create a group (e.g. “VIP customers”) and file contacts into it, then send to the whole group in one click."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((g) => {
              const active = groupFilter === String(g.groupId);
              return (
                <div
                  key={g.groupId}
                  className={`rounded-xl border p-4 transition ${
                    active ? "border-brand-400 bg-brand-50 dark:bg-brand-500/10" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button className="text-left" onClick={() => setGroupFilter(active ? "" : String(g.groupId))}>
                      <p className="font-semibold text-gray-900">{g.name}</p>
                      <p className="text-xs text-gray-500">{g.memberCount.toLocaleString()} contact(s)</p>
                    </button>
                    <Badge tone={active ? "green" : "gray"}>{active ? "Filtering" : "View"}</Badge>
                  </div>
                  {g.description && <p className="mt-1 text-xs text-gray-500">{g.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      href={`/sms/bulk?groupId=${g.groupId}`}
                      className="rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-pure-white transition hover:bg-brand-700"
                    >
                      Send to group
                    </Link>
                    <Button size="sm" variant="secondary" onClick={() => setGroupModal({ groupId: g.groupId, name: g.name, description: g.description ?? "" })}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteGroup(g)}>Delete</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Dynamic segments — a saved question, re-answered on every send. */}
      <Segments groups={groups} />

      {/* Add a contact */}
      <Card title="Add a contact">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-48">
            <Input label="Phone number" value={msisdn} onChange={(e) => setMsisdn(e.target.value.replace(/\D/g, "").slice(0, 15))} inputMode="tel" placeholder="2526XXXXXXX" />
          </div>
          <div className="w-full sm:w-48">
            <Input label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ali Hassan" />
          </div>
          <Button onClick={addContact} disabled={saving || !msisdn.trim()}>{saving ? "Saving…" : "Add contact"}</Button>
          {groupFilter && (
            <p className="text-xs text-gray-500">Will be added to “{groups.find((g) => String(g.groupId) === groupFilter)?.name}”.</p>
          )}
        </div>
      </Card>

      {/* Contacts */}
      <Card
        title={groupFilter ? `Contacts in “${groups.find((g) => String(g.groupId) === groupFilter)?.name}”` : "All contacts"}
        actions={
          <div className="flex items-center gap-2">
            <div className="w-full sm:w-48">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or number…" />
            </div>
            {groupFilter && <Button size="sm" variant="secondary" onClick={() => setGroupFilter("")}>Clear filter</Button>}
          </div>
        }
      >
        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-sm dark:border-brand-500/30 dark:bg-brand-500/10">
            <span className="font-medium text-gray-900">{selected.size} selected</span>
            {allSelected && hasMore && (
              // Selection (and every bulk action) covers only the contacts currently loaded — there
              // are more pages that "select all" can't reach. Make that explicit so a group action
              // isn't silently applied to a subset. Load them all with "Load more" first.
              <span className="text-xs text-gray-500">(loaded contacts only — use “Load more” to include the rest)</span>
            )}
            {groups.length > 0 && (
              <Select
                className="w-auto"
                value=""
                onChange={(e) => { if (e.target.value) addSelectedToGroup(Number(e.target.value)); }}
              >
                <option value="">Add to group…</option>
                {groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
              </Select>
            )}
            {groupFilter && <Button size="sm" variant="secondary" onClick={removeSelectedFromGroup}>Remove from this group</Button>}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        )}

        {contacts === null ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
        ) : allShown.length === 0 ? (
          <EmptyState title="No contacts" description="Add one above, or import a CSV to bring your whole list in at once." />
        ) : (
          <div>
            {/* "Load more" appends, so this list grows without bound. Past VIRTUALIZE_FROM rows we
                render only the on-screen slice and reserve the rest with spacer rows — the DOM
                stays at ~40 <tr>s no matter how many contacts are loaded. Below the threshold the
                table renders exactly as before (no scroll box, no fixed heights). */}
            <div
              ref={scrollRef}
              className={virtual.virtualized ? "max-h-[60vh] overflow-auto" : "overflow-x-auto"}
            >
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3 font-medium">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => setSelected(e.target.checked ? new Set(allShown.map((c) => c.contactId)) : new Set())}
                      />
                    </th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-4 font-medium">Number</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-4 font-medium">Name</th>
                    <th className="sticky top-0 z-10 hidden bg-white py-2 pr-4 font-medium lg:table-cell">Merge variables</th>
                    <th className="sticky top-0 z-10 hidden bg-white py-2 pr-4 font-medium sm:table-cell">Groups</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-4 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {virtual.topPad > 0 && <tr aria-hidden style={{ height: virtual.topPad }} />}

                  {allShown.slice(virtual.start, virtual.end).map((c) => (
                    // Fixed height + no wrapping: the windowing maths depends on every row being
                    // exactly ROW_HEIGHT tall.
                    <tr key={c.contactId} className="border-b border-gray-100" style={{ height: ROW_HEIGHT }}>
                      <td className="pr-3">
                        <input
                          type="checkbox"
                          checked={selected.has(c.contactId)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(c.contactId); else next.delete(c.contactId);
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td className="pr-4 font-medium tabular-nums text-gray-900">{c.msisdn}</td>
                      <td className="max-w-[8rem] truncate pr-4 text-gray-700 sm:max-w-[12rem]">{c.name || "—"}</td>
                      <td className="hidden pr-4 lg:table-cell">
                        <div className="flex gap-1 overflow-hidden whitespace-nowrap">
                          {c.attributes && Object.keys(c.attributes).length > 0
                            ? Object.keys(c.attributes).slice(0, 3).map((k) => <Badge key={k} tone="gray">{`{{${k}}}`}</Badge>)
                            : <span className="text-xs text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="hidden pr-4 text-xs text-gray-500 sm:table-cell">{c.groupCount ?? 0}</td>
                      <td className="pr-4 text-right">
                        <button onClick={() => removeContact(c)} className="text-xs font-medium text-danger-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}

                  {virtual.bottomPad > 0 && <tr aria-hidden style={{ height: virtual.bottomPad }} />}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing {allShown.length.toLocaleString()} contact{allShown.length === 1 ? "" : "s"}
                {hasMore ? " (more available)" : ""}
                {virtual.virtualized && (
                  <span className="ml-1 text-gray-400">
                    · rendering {virtual.end - virtual.start} of {allShown.length.toLocaleString()}
                  </span>
                )}
              </p>
              {hasMore && (
                <Button size="sm" variant="secondary" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Group create/edit */}
      <Modal
        open={Boolean(groupModal)}
        onClose={() => setGroupModal(null)}
        title={groupModal?.groupId ? "Edit group" : "New group"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setGroupModal(null)}>Cancel</Button>
            <Button onClick={saveGroup}>{groupModal?.groupId ? "Save" : "Create"}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Group name"
            value={groupModal?.name ?? ""}
            onChange={(e) => setGroupModal((g) => (g ? { ...g, name: e.target.value } : g))}
            placeholder="e.g. VIP customers"
            autoFocus
          />
          <Textarea
            label="Description (optional)"
            rows={2}
            value={groupModal?.description ?? ""}
            onChange={(e) => setGroupModal((g) => (g ? { ...g, description: e.target.value } : g))}
          />
        </div>
      </Modal>

      {/* CSV import */}
      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import contacts from CSV"
        widthClass="max-w-2xl"
        footer={
          <>
            <Button variant="secondary" disabled={importing} onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button onClick={runImport} disabled={importing || importPreview.length === 0}>
              {importing
                ? `Importing ${importProgress.toLocaleString()} / ${importPreview.length.toLocaleString()}…`
                : `Import ${importPreview.length.toLocaleString()} contact(s)`}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            First row is a header
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <Select label="Phone column" value={String(phoneCol)} onChange={(e) => setPhoneCol(Number(e.target.value))}>
              {importHeaders.map((h, i) => (
                <option key={i} value={i}>{hasHeader ? h || `Column ${i + 1}` : `Column ${i + 1}`}</option>
              ))}
            </Select>
            <Select label="Name column" value={String(nameCol)} onChange={(e) => setNameCol(Number(e.target.value))}>
              <option value="-1">— none —</option>
              {importHeaders.map((h, i) => (
                <option key={i} value={i}>{hasHeader ? h || `Column ${i + 1}` : `Column ${i + 1}`}</option>
              ))}
            </Select>
            <Select label="Add to group" value={importGroup} onChange={(e) => setImportGroup(e.target.value)}>
              <option value="">— none —</option>
              {groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
            </Select>
          </div>

          <p className="text-xs text-gray-500">
            Every other column is saved as a merge variable, so you can use them as{" "}
            <span className="font-mono">{"{{column_name}}"}</span> in a personalized send.
          </p>

          <div className="rounded-xl border border-gray-200 p-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Preview — {importPreview.length} valid number(s)
            </p>
            {importPreview.slice(0, 3).map((c) => (
              <p key={c.msisdn} className="truncate text-xs text-gray-600">
                <span className="font-medium text-gray-900">{c.msisdn}</span>
                {c.name ? ` · ${c.name}` : ""}
                {Object.keys(c.attributes).length > 0 ? ` · ${Object.keys(c.attributes).join(", ")}` : ""}
              </p>
            ))}
            {importPreview.length === 0 && <p className="text-xs text-gray-400">No valid numbers in the selected column.</p>}
          </div>
        </div>
      </Modal>
    </div>
  );
}
